// 설정 모달: 카테고리 추가/수정/삭제/드래그 정렬 + 비밀번호 변경
import { useState } from 'preact/hooks';
import type { Category } from '@shared/types';
import { api, ApiError } from '../core/ApiClient';
import { PasswordInput } from './PasswordInput';
import { buildCategoryTree, type CategoryNode } from '../core/categoryTree';

interface Props {
  categories: Category[];
  onClose: () => void;
  onCategoriesChanged: () => void;
}

type Tab = 'categories' | 'password';

export function SettingsPanel({ categories, onClose, onCategoriesChanged }: Props) {
  const [tab, setTab] = useState<Tab>('categories');

  return (
    <div class="modal-backdrop" onClick={onClose}>
      <div class="modal" onClick={(e) => e.stopPropagation()}>
        <div class="row" style="margin-bottom:16px">
          <h2 class="modal__title" style="margin:0">
            설정
          </h2>
          <div class="spacer" />
          <button class="btn btn--icon" onClick={onClose}>
            ✕
          </button>
        </div>

        <div class="row" style="margin-bottom:16px">
          <button
            class={`btn ${tab === 'categories' ? 'btn--primary' : ''}`}
            onClick={() => setTab('categories')}
          >
            카테고리
          </button>
          <button
            class={`btn ${tab === 'password' ? 'btn--primary' : ''}`}
            onClick={() => setTab('password')}
          >
            비밀번호
          </button>
        </div>

        {tab === 'categories' ? (
          <CategoryManager categories={categories} onChanged={onCategoriesChanged} />
        ) : (
          <PasswordChanger />
        )}
      </div>
    </div>
  );
}

function CategoryManager({
  categories,
  onChanged,
}: {
  categories: Category[];
  onChanged: () => void;
}) {
  const [items, setItems] = useState<Category[]>(categories);
  const [newName, setNewName] = useState('');
  const [dragId, setDragId] = useState<number | null>(null);
  const [subParent, setSubParent] = useState<number | null>(null);
  const [subName, setSubName] = useState('');
  const [err, setErr] = useState('');

  const tree = buildCategoryTree(items);

  const reload = async () => {
    const list = await api.listCategories();
    setItems(list);
    onChanged();
  };

  const add = async (name: string, parentId: number | null) => {
    const clean = name.trim();
    if (!clean) return;
    setErr('');
    try {
      await api.createCategory({ name: clean, parentId });
      await reload();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '추가 실패');
    }
  };

  const rename = async (id: number, name: string) => {
    const clean = name.trim();
    const current = items.find((c) => c.id === id);
    // 값이 비었거나 기존과 동일하면 불필요한 D1 쓰기 방지(변경 시에만 저장)
    if (!clean || !current || clean === current.name) return;
    await api.updateCategory(id, { name: clean });
    onChanged();
  };

  const remove = async (id: number) => {
    setErr('');
    if (!confirm('이 카테고리를 삭제하시겠습니까?')) return;
    try {
      await api.deleteCategory(id);
      await reload();
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '삭제 실패');
    }
  };

  // 드래그 정렬(같은 부모 형제끼리만)
  const onDragEnter = (targetId: number) => {
    if (dragId === null || dragId === targetId) return;
    const d = items.find((c) => c.id === dragId);
    const t = items.find((c) => c.id === targetId);
    if (!d || !t || d.parentId !== t.parentId) return;
    const next = [...items];
    const from = next.findIndex((c) => c.id === dragId);
    const to = next.findIndex((c) => c.id === targetId);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setItems(next);
  };

  const onDragEnd = async () => {
    if (dragId !== null) {
      const node = items.find((c) => c.id === dragId);
      const parentId = node?.parentId ?? null;
      const siblingIds = items.filter((c) => c.parentId === parentId).map((c) => c.id);
      await api.reorderCategories(siblingIds);
      await reload();
    }
    setDragId(null);
  };

  const renderNodes = (nodes: CategoryNode[]) => (
    <ul class="cat-tree">
      {nodes.map((node) => (
        <li key={node.id}>
          <div
            class={`cat-node ${node.id === dragId ? 'dragging' : ''}`}
            draggable
            onDragStart={() => setDragId(node.id)}
            onDragEnter={() => onDragEnter(node.id)}
            onDragOver={(e) => e.preventDefault()}
            onDragEnd={onDragEnd}
          >
            <span class="cat-item__grip">⠿</span>
            <input
              class="input"
              style="flex:1"
              value={node.name}
              onBlur={(e) => {
                const el = e.target as HTMLInputElement;
                if (el.value.trim() === node.name) return; // 변경 없음 → 저장·원복 모두 불필요
                if (!el.value.trim()) el.value = node.name; // 빈 값이면 원래 이름으로 원복
                else rename(node.id, el.value);
              }}
            />
            {node.depth < 3 && (
              <button
                class="btn btn--icon"
                title="하위 추가"
                onClick={() => {
                  setSubParent(node.id);
                  setSubName('');
                }}
              >
                ＋
              </button>
            )}
            <button class="btn btn--icon btn--danger" title="삭제" onClick={() => remove(node.id)}>
              ✕
            </button>
          </div>

          {subParent === node.id && (
            <div class="cat-sub-input">
              <input
                class="input"
                placeholder={`'${node.name}' 하위 카테고리 이름`}
                value={subName}
                autoFocus
                onInput={(e) => setSubName((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    add(subName, node.id);
                    setSubParent(null);
                  }
                  if (e.key === 'Escape') setSubParent(null);
                }}
              />
              <button
                class="btn btn--add btn--primary"
                onClick={() => {
                  add(subName, node.id);
                  setSubParent(null);
                }}
              >
                추가
              </button>
              <button class="btn btn--add" onClick={() => setSubParent(null)}>
                취소
              </button>
            </div>
          )}

          {node.children.length > 0 && renderNodes(node.children)}
        </li>
      ))}
    </ul>
  );

  return (
    <div>
      <p class="section__desc" style="margin-bottom:10px">
        최대 3단계까지 하위 카테고리를 만들 수 있습니다. (＋ 하위 추가 · 같은 단계끼리 드래그 정렬)
      </p>
      <div style="margin-bottom:16px">{renderNodes(tree)}</div>

      <div class="cat-add-row">
        <input
          class="input"
          placeholder="새 최상위 카테고리 이름"
          value={newName}
          onInput={(e) => setNewName((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              add(newName, null);
              setNewName('');
            }
          }}
        />
        <button
          class="btn btn--primary btn--add"
          onClick={() => {
            add(newName, null);
            setNewName('');
          }}
        >
          추가
        </button>
      </div>
      {err && <div class="msg-error">{err}</div>}
    </div>
  );
}

function PasswordChanger() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const submit = async () => {
    setMsg('');
    setErr('');
    if (next.length < 4) return setErr('새 비밀번호는 4자 이상이어야 합니다.');
    if (next !== confirm) return setErr('새 비밀번호가 일치하지 않습니다.');
    try {
      await api.changePassword({ currentPassword: current, newPassword: next });
      setMsg('비밀번호가 변경되었습니다.');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (ex) {
      setErr(ex instanceof ApiError ? ex.message : '변경 실패');
    }
  };

  return (
    <div>
      <div class="field">
        <label>현재 비밀번호</label>
        <PasswordInput value={current} onValue={setCurrent} />
      </div>
      <div class="field">
        <label>새 비밀번호</label>
        <PasswordInput value={next} onValue={setNext} />
      </div>
      <div class="field">
        <label>새 비밀번호 확인</label>
        <PasswordInput value={confirm} onValue={setConfirm} />
      </div>
      <div class="row row--end">
        <button class="btn btn--primary" onClick={submit}>
          변경
        </button>
      </div>
      {msg && <div style="color:var(--c-success);font-size:13px;margin-top:8px">{msg}</div>}
      {err && <div class="msg-error">{err}</div>}
    </div>
  );
}
